import { APP_WILL_UNMOUNT } from '../base/app/actionTypes';
import { CONFERENCE_JOINED, CONFERENCE_LEFT } from '../base/conference/actionTypes';
import ReducerRegistry from '../base/redux/ReducerRegistry';
import { getCaptionPreferences } from './captionPreferences';
import { CAPTION_PREFERENCES_CHANGED, REMOVE_TRANSCRIPT_MESSAGE, SET_REQUESTING_SUBTITLES, TOGGLE_REQUESTING_SUBTITLES, UPDATE_TRANSCRIPT_MESSAGE } from './actionTypes';
/**
 * Default State for 'features/transcription' feature.
 */
const defaultState = {
    _conference: undefined,
    _displaySubtitles: false,
    _transcriptMessages: new Map(),
    _finalizedTranscriptIds: new Set(),
    _requestingSubtitles: false,
    _language: null,
    subtitlesHistory: []
};
/**
 * Listen for actions for the transcription feature to be used by the actions
 * to update the rendered transcription subtitles.
 */
ReducerRegistry.register('features/subtitles', (state = defaultState, action) => {
    switch (action.type) {
        case CAPTION_PREFERENCES_CHANGED:
            return {
                ...state,
                subtitlesHistory: getCaptionPreferences().keepTranscript ? state.subtitlesHistory : []
            };
        case CONFERENCE_JOINED: {
            const conference = action.conference;
            if (!conference || typeof conference !== 'object' || Array.isArray(conference)
                || state._conference === conference)
                return state;
            return { ...state, _conference: conference, _transcriptMessages: new Map(), _finalizedTranscriptIds: new Set(), subtitlesHistory: [] };
        }
        case CONFERENCE_LEFT:
            if (action.conference && action.conference !== state._conference)
                return state;
            // Transcripts are promised to be local and in-memory to the call.
            // Letting one call's captions bleed into the next is a privacy leak
            // (the next participant's device renders the previous call's lines),
            // so both the live map and the retained history reset on leave;
            // display/requesting flags survive so preferences still apply to the
            // next room.
            return {
                ...state,
                _conference: undefined,
                _transcriptMessages: new Map(),
                _finalizedTranscriptIds: new Set(),
                subtitlesHistory: []
            };
        case APP_WILL_UNMOUNT:
            return { ...state, _conference: undefined, _transcriptMessages: new Map(), _finalizedTranscriptIds: new Set(), subtitlesHistory: [] };
        case REMOVE_TRANSCRIPT_MESSAGE:
            return _removeTranscriptMessage(state, action);
        case UPDATE_TRANSCRIPT_MESSAGE:
            return _updateTranscriptMessage(state, action);
        case SET_REQUESTING_SUBTITLES:
            return {
                ...state,
                _displaySubtitles: action.displaySubtitles,
                _language: action.language,
                _requestingSubtitles: action.enabled
            };
        case TOGGLE_REQUESTING_SUBTITLES:
            return {
                ...state,
                _requestingSubtitles: !state._requestingSubtitles
            };
    }
    return state;
});
/**
 * Reduces a specific Redux action REMOVE_TRANSCRIPT_MESSAGE of the feature
 * transcription.
 *
 * @param {Object} state - The Redux state of the feature transcription.
 * @param {Action} action -The Redux action REMOVE_TRANSCRIPT_MESSAGE to reduce.
 * @returns {Object} The new state of the feature transcription after the
 * reduction of the specified action.
 */
function _removeTranscriptMessage(state, { transcriptMessageID }) {
    const newTranscriptMessages = new Map(state._transcriptMessages);
    // Deletes the key from Map once a final message arrives.
    newTranscriptMessages.delete(transcriptMessageID);
    return {
        ...state,
        _transcriptMessages: newTranscriptMessages
    };
}
/**
 * Reduces a specific Redux action UPDATE_TRANSCRIPT_MESSAGE of the feature
 * transcription.
 *
 * @param {Object} state - The Redux state of the feature transcription.
 * @param {Action} action -The Redux action UPDATE_TRANSCRIPT_MESSAGE to reduce.
 * @returns {Object} The new state of the feature transcription after the
 * reduction of the specified action.
 */
function _updateTranscriptMessage(state, { transcriptMessageID, newTranscriptMessage }) {
    // Messages queued after the room or app closes cannot recreate its history.
    if (!state._conference)
        return state;
    // A completed phrase may be explicitly corrected by another final, but
    // delayed partial words cannot replace it or reopen it after display dwell.
    if (!newTranscriptMessage.final && (state._finalizedTranscriptIds?.has(transcriptMessageID)
        || state._transcriptMessages.get(transcriptMessageID)?.final))
        return state;
    let finalizedTranscriptIds = state._finalizedTranscriptIds ?? new Set();
    if (newTranscriptMessage.final) {
        finalizedTranscriptIds = new Set(finalizedTranscriptIds);
        finalizedTranscriptIds.delete(transcriptMessageID);
        finalizedTranscriptIds.add(transcriptMessageID);
        // Bound completion metadata independently of optional retained words.
        if (finalizedTranscriptIds.size > 500)
            finalizedTranscriptIds.delete(finalizedTranscriptIds.values().next().value);
    }
    const newTranscriptMessages = new Map(state._transcriptMessages);
    const keepTranscript = getCaptionPreferences().keepTranscript;
    const text = newTranscriptMessage.final
        || newTranscriptMessage.stable
        || newTranscriptMessage.unstable
        || '';
    let subtitlesHistory = keepTranscript ? state.subtitlesHistory : [];
    // Updates the new message for the given key in the Map.
    newTranscriptMessages.set(transcriptMessageID, newTranscriptMessage);
    if (text && keepTranscript) {
        const nextEntry = {
            captionTone: newTranscriptMessage.captionTone,
            final: Boolean(newTranscriptMessage.final),
            id: transcriptMessageID,
            language: newTranscriptMessage.language,
            participantId: newTranscriptMessage.participant.id,
            participantName: newTranscriptMessage.participant.name,
            text,
            timestamp: Date.now()
        };
        const existingIndex = subtitlesHistory.findIndex(entry => entry.id === transcriptMessageID);
        if (existingIndex >= 0) {
            subtitlesHistory = [...subtitlesHistory];
            subtitlesHistory[existingIndex] = nextEntry;
        }
        else {
            subtitlesHistory = [...subtitlesHistory, nextEntry].slice(-500);
        }
    }
    return {
        ...state,
        _transcriptMessages: newTranscriptMessages,
        _finalizedTranscriptIds: finalizedTranscriptIds,
        subtitlesHistory
    };
}
